// Comprehensive Design System for Hotel Devi Inventory Management
// Extracted from both Web (React/Material-UI) and Mobile (Flutter) applications
// Supports cross-platform consistency

import { createTheme } from '@mui/material/styles';

// ============================================================================
// COLOR PALETTE - Unified across Web and Mobile platforms
// ============================================================================

export const colors = {
  // Primary Brand Colors (Web)
  primary: {
    main: '#006494',      // Deep blue - used in web
    light: '#4B9FD8',
    dark: '#004266',
    contrastText: '#FFFFFF',
  },

  // Secondary Colors (Web)
  secondary: {
    main: '#8358A0',      // Purple
    light: '#AB7BC3',
    dark: '#5D3B72',
    contrastText: '#FFFFFF',
  },

  // Functional Colors (Web)
  production: {
    main: '#0f766e',      // Green
    light: '#14a085',
    dark: '#0a4a42',
    contrastText: '#FFFFFF',
  },

  reports: {
    main: '#1e3a8a',      // Blue
    light: '#4c63d2',
    dark: '#001064',
    contrastText: '#FFFFFF',
  },

  accent: {
    main: '#0d7377',      // Teal
    light: '#3da4ab',
    dark: '#004a4f',
    contrastText: '#FFFFFF',
  },

  info: {
    main: '#00BCD4',      // Cyan
    light: '#62efff',
    dark: '#008ba3',
    contrastText: '#FFFFFF',
  },

  // Status Colors
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
    contrastText: '#FFFFFF',
  },

  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
    contrastText: '#FFFFFF',
  },

  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
    contrastText: '#FFFFFF',
  },

  // Mobile App Colors (from Flutter)
  mobile: {
    textColor: '#312E29',        // Dark brown text
    dividerColor: '#01ACF1',     // Light blue divider
    buttonColor: '#343499',      // Dark blue button
    borderColor: '#C7C7C7',      // Light grey border
    hintColor: '#575864',        // Medium grey hint text
    backButtonColor: '#181C2E',  // Dark blue-black
    nonVegColor: '#9c3b14',      // Reddish brown
    sheetBgColor: '#f0f0f5',     // Light grey background
    circleColor: '#F0F5FA',      // Very light blue
    devGroupColor: '#CB395A',    // Pink-red
  },

  // Neutral Colors (Unified)
  background: {
    default: '#F8F9FA',    // Web background
    paper: '#FFFFFF',
    mobile: '#f0f0f5',    // Mobile sheet background
  },

  grey: {
    50: '#F8F9FA',
    100: '#ECEFF1',
    200: '#CFD8DC',
    300: '#B0BEC5',
    400: '#90A4AE',
    500: '#78909C',
    600: '#607D8B',
    700: '#546E7A',
    800: '#37474F',
    900: '#263238',
  },

  // Text Colors (Unified)
  text: {
    primary: '#263238',      // Web primary
    secondary: '#546E7A',    // Web secondary
    mobile: '#312E29',       // Mobile primary
    hint: '#575864',         // Mobile hint
    disabled: '#B0BEC5',
  },
};

// ============================================================================
// TYPOGRAPHY SYSTEM
// ============================================================================

export const typography = {
  // Web Typography (Inter font)
  web: {
    fontFamily: ['Inter', 'Roboto', 'Arial', 'sans-serif'].join(','),
    h1: { fontSize: '2.5rem', fontWeight: 600, lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.2 },
    h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.2 },
    h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.2 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.2 },
    body1: { fontSize: '0.95rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 500 },
    caption: { fontSize: '0.75rem', lineHeight: 1.4 },
  },

  // Mobile Typography (Poppins font)
  mobile: {
    fontFamily: 'Poppins, sans-serif',
    h1: { fontSize: '22px', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '18px', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '16px', fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '15px', fontWeight: 400, lineHeight: 1.5 },
    body2: { fontSize: '14px', fontWeight: 400, lineHeight: 1.4 },
    caption: { fontSize: '12px', fontWeight: 400, lineHeight: 1.4 },
  },

  // Unified Typography (defaults to web)
  fontFamily: ['Inter', 'Poppins', 'Roboto', 'Arial', 'sans-serif'].join(','),
  h4: { fontSize: '1.5rem', fontWeight: 600 },
  h5: { fontSize: '1.25rem', fontWeight: 600 },
  h6: { fontSize: '1rem', fontWeight: 600 },
  button: { textTransform: 'none', fontWeight: 500 },
  subtitle1: { fontSize: '1rem', fontWeight: 500 },
  body1: { fontSize: '0.95rem' },
};

// ============================================================================
// SPACING SYSTEM
// ============================================================================

export const spacing = {
  // Standard scale
  xs: 4,      // 4px
  sm: 8,      // 8px
  md: 16,     // 16px
  lg: 24,     // 24px
  xl: 32,     // 32px
  xxl: 48,    // 48px
  xxxl: 64,   // 64px

  // Mobile specific (from Flutter)
  mobile: {
    tiny: 4,
    small: 8,
    medium: 16,
    large: 24,
    extraLarge: 32,
  },

  // Layout spacing
  page: 16,   // Page padding
  card: 16,   // Card padding
  gap: 12,    // Grid gap
};

// ============================================================================
// BORDER RADIUS SYSTEM
// ============================================================================

export const borderRadius = {
  none: 0,
  sm: 4,      // Small elements
  md: 6,      // Default for most components
  lg: 8,      // Cards, dialogs
  xl: 12,     // Large containers
  xxl: 16,    // Special cases
  full: 9999, // Fully rounded
};

// ============================================================================
// SHADOWS SYSTEM
// ============================================================================

export const shadows = {
  none: 'none',
  sm: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0px 2px 8px rgba(0, 0, 0, 0.08)',    // Web cards
  lg: '0px 4px 16px rgba(0, 0, 0, 0.12)',   // Elevated elements
  xl: '0px 8px 24px rgba(0, 0, 0, 0.15)',   // Dialogs, dropdowns
  xxl: '0px 16px 32px rgba(0, 0, 0, 0.2)',  // Special emphasis

  // Mobile shadows (Material Design)
  mobile: {
    elevation1: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation2: '0px 4px 8px rgba(0, 0, 0, 0.12)',
    elevation3: '0px 6px 12px rgba(0, 0, 0, 0.15)',
  },
};

// ============================================================================
// BREAKPOINTS (Responsive Design)
// ============================================================================

export const breakpoints = {
  xs: 0,      // Mobile
  sm: 600,    // Small tablet
  md: 960,    // Tablet
  lg: 1280,   // Desktop
  xl: 1920,   // Large desktop
};

// ============================================================================
// MATERIAL-UI THEME CONFIGURATION (Web)
// ============================================================================

export const componentOverrides = {
  MuiCard: {
    styleOverrides: {
      root: {
        boxShadow: shadows.md,
        borderRadius: borderRadius.md,
        padding: 0,
        overflow: 'hidden',
        border: '1px solid rgba(0, 0, 0, 0.08)',
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: spacing.lg,
        '&:last-child': { paddingBottom: spacing.lg },
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.md,
        padding: `${spacing.sm}px ${spacing.lg}px`,
        fontWeight: 500,
        textTransform: 'none',
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: shadows.md,
          transform: 'translateY(-1px)',
        },
        '&:active': { transform: 'translateY(0)' },
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: borderRadius.md,
          padding: 0,
          '& input': {
            padding: `${spacing.md}px ${spacing.lg}px`,
            fontSize: '0.95rem',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.12)',
            borderWidth: 1,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
            borderColor: colors.primary.main,
          },
        },
        '& .MuiInputLabel-root': {
          '&.MuiInputLabel-outlined': {
            transform: `translate(${spacing.lg}px, ${spacing.md}px) scale(1)`,
            fontSize: '0.95rem',
          },
          '&.MuiInputLabel-shrink': {
            transform: `translate(${spacing.lg}px, -9px) scale(0.75)`,
            fontWeight: 500,
          },
        },
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: `${spacing.md}px ${spacing.lg}px`,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
      },
      head: {
        fontWeight: 600,
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        borderBottom: '2px solid rgba(0, 0, 0, 0.08)',
      },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        transition: 'background-color 0.2s ease',
        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
        '&:last-child td': { borderBottom: 0 },
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: borderRadius.lg,
        boxShadow: shadows.xl,
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: shadows.md,
        backgroundColor: colors.background.paper,
        color: colors.text.primary,
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
      },
    },
    defaultProps: { elevation: 0 },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: '1px solid rgba(0, 0, 0, 0.08)',
        boxShadow: shadows.lg,
      },
    },
  },
};

// ============================================================================
// FLUTTER THEME CONFIGURATION (Mobile)
// ============================================================================

export const flutterTheme = {
  // Colors
  primaryColor: colors.primary.main,
  scaffoldBackgroundColor: colors.background.mobile,
  cardColor: colors.background.paper,

  // AppBar
  appBarTheme: {
    backgroundColor: colors.background.paper,
    foregroundColor: colors.text.primary,
    elevation: 0,
    shadowColor: 'rgba(0, 0, 0, 0.08)',
  },

  // Text Theme (Poppins)
  textTheme: {
    headlineLarge: {
      fontSize: 22,
      fontWeight: 'w700',
      color: colors.mobile.textColor,
      fontFamily: 'Poppins',
    },
    headlineMedium: {
      fontSize: 18,
      fontWeight: 'w600',
      color: colors.mobile.textColor,
      fontFamily: 'Poppins',
    },
    bodyLarge: {
      fontSize: 16,
      fontWeight: 'w400',
      color: colors.mobile.textColor,
      fontFamily: 'Poppins',
    },
    bodyMedium: {
      fontSize: 15,
      fontWeight: 'w400',
      color: colors.text.primary,
      fontFamily: 'Poppins',
    },
    bodySmall: {
      fontSize: 14,
      fontWeight: 'w400',
      color: colors.text.secondary,
      fontFamily: 'Poppins',
    },
  },

  // Button Themes
  elevatedButtonTheme: {
    style: {
      backgroundColor: colors.mobile.buttonColor,
      foregroundColor: colors.background.paper,
      elevation: 2,
      shadowColor: 'rgba(0, 0, 0, 0.1)',
      padding: '12px 24px',
      borderRadius: borderRadius.md,
    },
  },

  // Input Decoration
  inputDecorationTheme: {
    border: {
      type: 'outline',
      borderRadius: borderRadius.md,
      borderSide: {
        color: colors.mobile.borderColor,
        width: 1,
      },
    },
    focusedBorder: {
      borderSide: {
        color: colors.primary.main,
        width: 2,
      },
    },
    contentPadding: '16px',
    hintStyle: {
      color: colors.mobile.hintColor,
      fontSize: 14,
    },
  },

  // Card Theme
  cardTheme: {
    color: colors.background.paper,
    shadowColor: 'rgba(0, 0, 0, 0.08)',
    elevation: 2,
    margin: spacing.mobile.medium,
    shape: {
      type: 'rounded',
      borderRadius: borderRadius.md,
    },
  },
};

// ============================================================================
// UNIFIED MATERIAL-UI THEME
// ============================================================================

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    production: colors.production,
    reports: colors.reports,
    accent: colors.accent,
    info: colors.info,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    background: colors.background,
    grey: colors.grey,
    text: colors.text,
  },
  typography,
  shape: { borderRadius: borderRadius.md },
  spacing: 8,  // Standard theme spacing unit, allows fractional values like 0.5
  breakpoints: { values: breakpoints },
  components: componentOverrides,
});

// ============================================================================
// GLOBAL CSS STYLES (Web)
// ============================================================================

export const globalStyles = `
  /* Font Imports */
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

  /* CSS Reset and Base Styles */
  * {
    box-sizing: border-box;
  }

  html {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    background-color: ${colors.background.default};
    line-height: 1.6;
    color: ${colors.text.primary};
  }

  #root {
    min-height: 100vh;
    width: 100%;
  }

  /* Custom Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${colors.grey[100]};
  }

  ::-webkit-scrollbar-thumb {
    background: ${colors.grey[400]};
    border-radius: ${borderRadius.sm}px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${colors.grey[500]};
  }

  /* Smooth Transitions */
  * {
    transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  }

  /* Focus Styles */
  button:focus-visible,
  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible {
    outline: 2px solid ${colors.primary.main};
    outline-offset: 2px;
  }

  /* Button Reset */
  button {
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
  }

  /* Typography Improvements */
  h1, h2, h3, h4, h5, h6 {
    margin: 0;
    font-weight: 600;
    line-height: 1.2;
    color: ${colors.text.primary};
  }

  p {
    margin: 0;
    color: ${colors.text.primary};
  }

  /* Link Styles */
  a {
    color: ${colors.primary.main};
    text-decoration: none;
    transition: color 0.2s ease;
  }

  a:hover {
    text-decoration: underline;
    color: ${colors.primary.dark};
  }

  /* Form Elements */
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  /* Utility Classes */
  .text-center { text-align: center; }
  .text-left { text-align: left; }
  .text-right { text-align: right; }

  .flex { display: flex; }
  .flex-col { flex-direction: column; }
  .flex-row { flex-direction: row; }
  .items-center { align-items: center; }
  .justify-center { justify-content: center; }
  .justify-between { justify-content: space-between; }
  .gap-1 { gap: ${spacing.xs}px; }
  .gap-2 { gap: ${spacing.sm}px; }
  .gap-3 { gap: ${spacing.md}px; }
  .gap-4 { gap: ${spacing.lg}px; }

  .p-1 { padding: ${spacing.xs}px; }
  .p-2 { padding: ${spacing.sm}px; }
  .p-3 { padding: ${spacing.md}px; }
  .p-4 { padding: ${spacing.lg}px; }

  .m-1 { margin: ${spacing.xs}px; }
  .m-2 { margin: ${spacing.sm}px; }
  .m-3 { margin: ${spacing.md}px; }
  .m-4 { margin: ${spacing.lg}px; }

  .shadow-sm { box-shadow: ${shadows.sm}; }
  .shadow-md { box-shadow: ${shadows.md}; }
  .shadow-lg { box-shadow: ${shadows.lg}; }
  .shadow-xl { box-shadow: ${shadows.xl}; }

  .rounded-sm { border-radius: ${borderRadius.sm}px; }
  .rounded-md { border-radius: ${borderRadius.md}px; }
  .rounded-lg { border-radius: ${borderRadius.lg}px; }
  .rounded-full { border-radius: ${borderRadius.full}px; }

  /* Mobile-specific colors */
  .text-mobile-primary { color: ${colors.mobile.textColor}; }
  .text-mobile-hint { color: ${colors.mobile.hintColor}; }
  .bg-mobile-sheet { background-color: ${colors.mobile.sheetBgColor}; }
  .border-mobile { border-color: ${colors.mobile.borderColor}; }
`;

// ============================================================================
// COMPONENT EXAMPLES AND PATTERNS
// ============================================================================

export const componentExamples = {
  // Web Components
  primaryButton: {
    variant: 'contained',
    color: 'primary',
    sx: { minWidth: 120 },
  },

  secondaryButton: {
    variant: 'outlined',
    color: 'secondary',
    sx: { minWidth: 120 },
  },

  dataCard: {
    sx: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
  },

  formField: {
    fullWidth: true,
    margin: 'normal',
    sx: { mb: 2 },
  },

  dataTable: {
    sx: {
      '& .MuiTableCell-root': {
        borderBottom: `1px solid ${colors.grey[200]}`,
      },
    },
  },

  pageContainer: {
    sx: {
      p: 3,
      maxWidth: 1200,
      mx: 'auto',
    },
  },

  sidebarContainer: {
    sx: {
      width: 280,
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        width: 280,
        boxSizing: 'border-box',
      },
    },
  },

  // Mobile Components (Flutter style descriptions)
  mobileCard: {
    margin: spacing.mobile.medium,
    elevation: 2,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background.paper,
  },

  mobileButton: {
    backgroundColor: colors.mobile.buttonColor,
    foregroundColor: colors.background.paper,
    borderRadius: borderRadius.md,
    padding: '12px 24px',
    elevation: 2,
  },

  mobileInput: {
    borderColor: colors.mobile.borderColor,
    borderRadius: borderRadius.md,
    focusedBorderColor: colors.primary.main,
    contentPadding: '16px',
  },
};

// ============================================================================
// RESPONSIVE UTILITIES
// ============================================================================

export const responsive = {
  // Container widths
  container: {
    sm: '100%',
    md: '768px',
    lg: '1024px',
    xl: '1200px',
  },

  // Grid columns
  grid: {
    cols: {
      sm: 1,
      md: 2,
      lg: 3,
      xl: 4,
    },
  },

  // Spacing responsive
  spacing: {
    page: {
      xs: spacing.sm,
      sm: spacing.md,
      md: spacing.lg,
      lg: spacing.xl,
    },
  },
};

export default theme;